import {yupResolver} from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import {Controller, useForm} from 'react-hook-form';
import {Form, Popup, List, Container, Icon, Button} from 'semantic-ui-react';
import * as yup from 'yup';

import {useReservedHostnames} from 'hooks';

import GroupSearchField from '../Common/Forms/GroupSearchField';
import {categoryOptions, categoryPurposes, validationSchemas} from '../Common/sharedElements';

import styles from './PaaSCreation.module.scss';

const formSchema = yup.object().shape({
  category: validationSchemas.categoryValidation,
  name: validationSchemas.nameValidation.test(
    'domainIsUnique',
    'Such domain already exists',
    (value, ctx) => {
      if (ctx.options.context.reservedHostnames.includes(value)) {
        return ctx.createError({message: 'Provided project name is a reserved name'});
      }

      return true;
    }
  ),
  adminGroup: yup.string(),
  description: validationSchemas.descriptionValidation,
  computingRulesApproved: validationSchemas.computingRulesApproved,
});

const defaultFormValues = {
  category: 'Official',
  name: '',
  adminGroup: '',
  description: '',
  computingRulesApproved: false,
};

const PaaSCreationForm = props => {
  const {reservedHostnames, isLoading: isLoadingReservedHostnames} = useReservedHostnames('paas');

  const {
    control,
    handleSubmit,
    formState: {isValid, isSubmitting},
    watch,
  } = useForm({
    context: {reservedHostnames},
    mode: 'onChange',
    defaultValues: defaultFormValues,
    resolver: yupResolver(formSchema),
  });

  const [categoryValue, adminGroupValue] = watch(['category', 'adminGroup']);

  // Form submission handler, sends a POST request via a function passed from parent component in props.create
  const submitHandler = async data => {
    const payload = {
      name: data.name,
      description: data.description,
      category: data.category,
    };

    if (data.adminGroup) {
      payload.adminGroup = data.adminGroup;
    }

    await props.create(payload);
  };

  return (
    <Container className={styles.PaaSCreationForm}>
      <a
        href="https://paas.docs.cern.ch/1._Getting_Started/1-create-paas-project/"
        target="_blank"
        rel="noreferrer"
      >
        Our recommendations for choosing a project name and category{' '}
        <Icon name="external alternate" size="small" />
      </a>

      <Form onSubmit={handleSubmit(submitHandler)} loading={isSubmitting}>
        <Controller
          control={control}
          name="category"
          render={({field: {onChange, value, name}, fieldState: {error}}) => (
            <Popup
              trigger={
                <Form.Dropdown
                  onChange={(_evt, data) => onChange(data.value)}
                  name={name}
                  value={value}
                  options={categoryOptions}
                  error={error?.message}
                  fluid
                  selection
                  required
                />
              }
              position="left center"
              wide
            >
              <Popup.Header>Specify project's category:</Popup.Header>
              <Popup.Content>
                <List items={categoryPurposes} />
                <p>The category can be changed later.</p>
              </Popup.Content>
            </Popup>
          )}
        />

        <Controller
          control={control}
          name="name"
          render={({field: {onChange, value, name}, fieldState: {error}}) => (
            <Popup
              trigger={
                <Form.Input
                  id="name"
                  error={error?.message}
                  required
                  onChange={(_evt, data) => onChange(data.value)}
                  name={name}
                  value={value}
                  placeholder="Project name"
                  label={{
                    htmlFor: 'name',
                    className: styles.VisuallyHidden,
                    children: 'Project name',
                  }}
                />
              }
              position="left center"
            >
              <Popup.Content>
                Choose descriptive and short name, following naming conventions.
              </Popup.Content>
            </Popup>
          )}
        />

        <Controller
          control={control}
          name="adminGroup"
          render={({field: {onChange, value, name}, fieldState: {error}}) => (
            <GroupSearchField
              id="adminGroup"
              name={name}
              value={value}
              onChange={data => {
                onChange(data.groupIdentifier ? data.groupIdentifier : '');
              }}
              error={error?.message}
              showRecommendation={categoryValue === 'Official' && !adminGroupValue}
            />
          )}
        />

        <Controller
          control={control}
          name="description"
          render={({field: {onChange, value, name}, fieldState: {error}}) => (
            <Popup
              trigger={
                <Form.Input
                  id="description"
                  label={{
                    htmlFor: 'description',
                    className: styles.VisuallyHidden,
                    children: 'Project description',
                  }}
                  onChange={(_evt, data) => onChange(data.value)}
                  name={name}
                  value={value}
                  error={error?.message}
                  placeholder="Project description"
                  required
                />
              }
              position="left center"
            >
              <Popup.Content>Description of the project's purpose.</Popup.Content>
            </Popup>
          )}
        />

        <Controller
          control={control}
          name="computingRulesApproved"
          render={({field: {onChange, value, name}}) => (
            <Form.Checkbox
              id="computingRules"
              onChange={(_evt, data) => onChange(data.checked)}
              name={name}
              checked={value}
              label={
                <label htmlFor="computingRules">
                  I have read and agreed to the{' '}
                  <a
                    href="https://security.web.cern.ch/security/rules/en/index.shtml"
                    target="_blank"
                    rel="noreferrer"
                  >
                    CERN Computing Rules
                  </a>{' '}
                  and taken into account the{' '}
                  <a
                    href="http://design-guidelines.web.cern.ch/guidelines-cern-websites"
                    target="_blank"
                    rel="noreferrer"
                  >
                    design guidelines
                  </a>{' '}
                  for websites and the{' '}
                  <a
                    href="https://paas.docs.cern.ch/1._Getting_Started/1-create-paas-project/#project-lifecycle"
                    target="_blank"
                    rel="noreferrer"
                  >
                    website lifecycle policy
                  </a>
                  .
                </label>
              }
            />
          )}
        />
        <Button
          type="submit"
          className={styles.SaveButton}
          floated="right"
          disabled={!isValid || isLoadingReservedHostnames || isSubmitting}
        >
          Create
        </Button>
      </Form>
    </Container>
  );
};

PaaSCreationForm.propTypes = {
  create: PropTypes.func.isRequired,
};

export default PaaSCreationForm;
